import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Avatar,
  Badge,
  Button,
  EmptyState,
  Filters,
  LoadingState,
  Pagination,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createUsersApi } from "../api";
import { accountStatusTone, formatDate, formatLabel, tierName } from "../labels";
import type { UserAccountStatus, UserListItemDto } from "../types";
import { MergeUsersDialog } from "../components/MergeUsersDialog";
import { UserPreviewDrawer } from "../components/UserPreviewDrawer";
import { useToast } from "@/components/ui";

const ACCOUNT_STATUSES: UserAccountStatus[] = [
  "created",
  "active",
  "restricted",
  "suspended",
  "banned",
  "closed",
];

const TIERS = [0, 1, 2, 3];

const SORT_OPTIONS = [
  { value: "display_name", label: "Display name (A–Z)" },
  { value: "-display_name", label: "Display name (Z–A)" },
  { value: "account_status", label: "Account status" },
  { value: "public_id", label: "Public ID" },
  { value: "id", label: "ID" },
];

export function UsersDirectoryPage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Users" },
  ]);

  const client = useApiClient();
  const api = useMemo(() => createUsersApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") ?? "";
  const accountStatus = (searchParams.get("account_status") ??
    "") as UserAccountStatus | "";
  const sort = searchParams.get("sort") ?? "display_name";
  const cursor = searchParams.get("cursor") ?? undefined;
  const sport = searchParams.get("sport") ?? "";
  const tier = searchParams.get("tier") ?? "";
  const verified = (searchParams.get("verified") ?? "") as "true" | "false" | "";

  const [users, setUsers] = useState<UserListItemDto[]>([]);
  const [sportOptions, setSportOptions] = useState<
    Array<{ sport: string; players: number }>
  >([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(q);
  const [previewUser, setPreviewUser] = useState<UserListItemDto | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);

  const page = cursorStack.length + 1;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.list({
        ...(cursor ? { cursor } : {}),
        limit: 25,
        sort,
        ...(q ? { q } : {}),
        ...(accountStatus ? { account_status: accountStatus } : {}),
        ...(sport ? { sport } : {}),
        ...(tier ? { tier } : {}),
        ...(verified ? { verified } : {}),
      });
      setUsers(response.users);
      setNextCursor(response.pagination.next_cursor);
    } catch (err) {
      setError(apiErrorMessage(err));
      setUsers([]);
      setNextCursor(undefined);
    } finally {
      setLoading(false);
    }
  }, [api, cursor, sort, q, accountStatus, sport, tier, verified]);

  useEffect(() => {
    void load();
  }, [load]);

  // The sports filter lists what players actually picked, not a fixed catalogue.
  useEffect(() => {
    let live = true;
    api
      .sportFacets()
      .then((r) => {
        if (live) setSportOptions(r.sports);
      })
      .catch(() => {
        if (live) setSportOptions([]);
      });
    return () => {
      live = false;
    };
  }, [api]);

  useEffect(() => {
    setSearchDraft(q);
  }, [q]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  function applySearch() {
    setCursorStack([]);
    updateParams({ q: searchDraft.trim() || undefined, cursor: undefined });
  }

  function goNext() {
    if (!nextCursor) return;
    setCursorStack((stack) => [...stack, cursor ?? ""]);
    updateParams({ cursor: nextCursor });
  }

  function goPrevious() {
    if (cursorStack.length === 0) return;
    const stack = [...cursorStack];
    const previous = stack.pop();
    setCursorStack(stack);
    updateParams({
      cursor: previous && previous.length > 0 ? previous : undefined,
    });
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Users"
        description="Locate canonical Users for authorized HQ work. Account actions are never bulk actions."
        actions={
          <Flex gap={8}>
            <Button variant="secondary" onClick={() => void navigate("/app/creators?tab=applications")}>
              Applications
            </Button>
            <Button variant="secondary" onClick={() => setMergeOpen(true)}>
              Merge Users
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      <Filters>
        <div style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Search
            value={searchDraft}
            onValueChange={setSearchDraft}
            placeholder="Search name, @handle, email, phone, public ID"
            onKeyDown={(event) => {
              if (event.key === "Enter") applySearch();
            }}
          />
        </div>
        <Select
          label="Account status"
          value={accountStatus}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              account_status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All statuses" },
            ...ACCOUNT_STATUSES.map((status) => ({
              value: status,
              label: formatLabel(status),
            })),
          ]}
        />
        <Select
          label="Sport"
          value={sport}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ sport: event.target.value || undefined, cursor: undefined });
          }}
          options={[
            { value: "", label: "Any sport" },
            ...sportOptions.map((o) => ({
              value: o.sport,
              label: `${o.sport} (${o.players})`,
            })),
          ]}
        />
        <Select
          label="Tier"
          value={tier}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ tier: event.target.value || undefined, cursor: undefined });
          }}
          options={[
            { value: "", label: "Any tier" },
            ...TIERS.map((t) => ({ value: String(t), label: tierName(t) })),
          ]}
        />
        <Select
          label="Identity"
          value={verified}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ verified: event.target.value || undefined, cursor: undefined });
          }}
          options={[
            { value: "", label: "Any" },
            { value: "true", label: "Verified" },
            { value: "false", label: "Not verified" },
          ]}
        />
        <Select
          label="Sort"
          value={sort}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ sort: event.target.value, cursor: undefined });
          }}
          options={SORT_OPTIONS}
        />
        <Button variant="primary" onClick={applySearch}>
          Apply search
        </Button>
      </Filters>

      {error ? (
        <Alert tone="danger" title="Unable to load Users">
          {error}
        </Alert>
      ) : null}

      {loading ? <LoadingState message="Loading Users…" /> : null}

      {!loading && !error && users.length === 0 ? (
        <EmptyState
          reason={q || accountStatus || sport || tier || verified ? "filters" : "none"}
          title="No Users found"
          action={
            <Button
              variant="secondary"
              onClick={() => {
                setCursorStack([]);
                setSearchParams(new URLSearchParams());
              }}
            >
              Clear filters
            </Button>
          }
        />
      ) : null}

      {!loading && users.length > 0 ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Showing {users.length} User{users.length === 1 ? "" : "s"}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) goNext();
                  if (nextPage < page) goPrevious();
                }}
              />
            }
          />
          <Table>
            <Thead>
              <Tr>
                <Th>User</Th>
                <Th>Sports</Th>
                <Th>Location</Th>
                <Th>Tier</Th>
                <Th>Referred</Th>
                <Th>Code used</Th>
                <Th>Joined</Th>
                <Th>Account status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((user) => (
                <Tr key={user.id}>
                  <Td>
                    <Flex gap={12} align="center">
                      <Avatar
                        name={user.play?.name || user.display_name || "?"}
                        {...(user.play?.avatar ? { src: user.play.avatar } : {})}
                        style={{ width: 36, height: 36 }}
                      />
                      <div>
                        <Text role="bodyStrong" as="div">
                          {user.play?.name || user.display_name || "Unnamed User"}
                          {user.play?.verified ? (
                            <Badge tone="success" style={{ marginLeft: 6 }}>
                              Verified
                            </Badge>
                          ) : null}
                        </Text>
                        <Text role="caption" color="muted" as="div">
                          {user.play ? `@${user.play.handle} · ` : ""}
                          {user.email ?? user.phone ?? user.public_id}
                        </Text>
                      </div>
                    </Flex>
                  </Td>
                  <Td>
                    {user.play && user.play.sports.length > 0 ? (
                      <Flex gap={4} wrap>
                        {user.play.sports.slice(0, 3).map((name) => (
                          <Badge key={name} tone="info">
                            {name}
                          </Badge>
                        ))}
                        {user.play.sports.length > 3 ? (
                          <Badge tone="neutral">+{user.play.sports.length - 3}</Badge>
                        ) : null}
                      </Flex>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>{user.play?.location || "—"}</Td>
                  <Td>{user.play ? tierName(user.play.tier) : "—"}</Td>
                  <Td>{user.referral?.referred_count ?? 0}</Td>
                  <Td>{user.referral?.code_used ? `@${user.referral.code_used}` : "—"}</Td>
                  <Td>{user.play ? formatDate(user.play.created_at) : "—"}</Td>
                  <Td>
                    <StatusChip tone={accountStatusTone(user.account_status)}>
                      {formatLabel(user.account_status)}
                    </StatusChip>
                  </Td>
                  <Td>
                    <Flex gap={8}>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setPreviewUser(user)}
                      >
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => void navigate(`/app/users/${user.id}`)}
                      >
                        Open
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Stack>
      ) : null}

      <UserPreviewDrawer
        open={previewUser !== null}
        user={previewUser}
        onClose={() => setPreviewUser(null)}
      />

      <MergeUsersDialog
        open={mergeOpen}
        onClose={() => {
          setMergeOpen(false);
          setMergeError(null);
        }}
        busy={mergeBusy}
        error={mergeError}
        onSubmit={async (input) => {
          setMergeBusy(true);
          setMergeError(null);
          try {
            await api.mergeUsers({
              survivor_user_id: input.survivorUserId,
              duplicate_user_id: input.duplicateUserId,
              reason: input.reason,
              approval_id: input.approvalId,
            });
            toast.push({
              title: "Users merged",
              description: "Survivor retained; duplicate closed per backend rules.",
              tone: "success",
            });
            setMergeOpen(false);
            void load();
          } catch (err) {
            setMergeError(apiErrorMessage(err));
          } finally {
            setMergeBusy(false);
          }
        }}
      />
    </Stack>
  );
}
